import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, isApiError } from '@/lib/api';

export type LocumContractStatus =
  'invited' | 'upcoming' | 'active' | 'ended' | 'cancelled';

export type LocumRateBasis = 'hourly' | 'per_session';

export interface LocumContract {
  id: string;
  locumUserId: string | null;
  fullName: string;
  email: string;
  branchId: string;
  branchName: string;
  startsOn: string;
  endsOn: string;
  rateBasis: LocumRateBasis;
  rateSen: number;
  status: LocumContractStatus;
  notes: string | null;
  acceptedAt: string | null;
}

export interface SaveLocumContractInput {
  id?: string;
  locumUserId: string;
  branchId: string;
  startsOn: string;
  endsOn: string;
  rateBasis: LocumRateBasis;
  rateSen: number;
  notes?: string;
}

export type LocumContractErrorKind = 'not_found' | 'invalid_rate' | null;

const INVALID_RATE_CODES = new Set([
  'INVALID_LOCUM_RATE',
  'INVALID_RATE',
  'INVALID_RATE_PAYLOAD',
  'LOCUM_RATE_INVALID',
  'LOCUM_CONTRACT_INVALID_RATE',
  'LOCUM_CONTRACT_INVALID_RATE_PAYLOAD',
  'LOCUM_CONTRACT_RATE_INVALID',
]);

/** Stable contract failures that need actionable guidance instead of API prose. */
export function locumContractErrorKind(error: unknown): LocumContractErrorKind {
  if (!isApiError(error)) return null;
  if (error.code === 'LOCUM_CONTRACT_NOT_FOUND') return 'not_found';
  if (INVALID_RATE_CODES.has(error.code)) return 'invalid_rate';
  if (
    error.code === 'VALIDATION_FAILED' &&
    ['rateBasis', 'rateSen'].some((field) => error.fieldError(field))
  )
    return 'invalid_rate';
  return null;
}

const locumKeys = {
  all: ['locums'] as const,
  mine: ['locums', 'mine'] as const,
  management: ['locums', 'management'] as const,
};

/** The signed-in locum's current or most recent contract. */
export function useMyLocumContract(enabled = true) {
  return useQuery({
    queryKey: locumKeys.mine,
    queryFn: ({ signal }) =>
      api
        .get<LocumContract | null>('/locum/contract', { signal })
        .then((response) => response.data),
    enabled,
  });
}

/** Contracts visible to clinic managers and super admins. */
export function useLocums(enabled = true) {
  return useQuery({
    queryKey: locumKeys.management,
    queryFn: ({ signal }) =>
      api
        .get<LocumContract[]>('/admin/locums', { signal })
        .then((response) => response.data),
    enabled,
  });
}

/** RL-1800: a locum the manager can give a contract to. */
export interface LocumPerson {
  id: string;
  fullName: string;
  email: string;
  /** The branches the person is a locum at; null = every branch. */
  branchIds: string[] | null;
}

/** RL-1800: GET /admin/locums/people (locums:read, no staff:read needed). */
export function useLocumPeople(enabled = true) {
  return useQuery({
    queryKey: [...locumKeys.all, 'people'],
    queryFn: ({ signal }) =>
      api
        .get<LocumPerson[]>('/admin/locums/people', { signal })
        .then((response) => response.data),
    enabled,
  });
}

/** Create or edit a contract for an existing staff record. */
export function useSaveLocumContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, locumUserId, ...input }: SaveLocumContractInput) =>
      (id
        ? api.patch<LocumContract>(`/admin/locums/${id}`, input)
        : api.post<LocumContract>('/admin/locums', {
            ...input,
            locumUserId,
          })
      ).then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: locumKeys.all }),
  });
}

export function useEndLocumContract() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api
        .post<LocumContract>(`/admin/locums/${id}/end`)
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: locumKeys.all }),
  });
}
